import { useState } from "react";
import { useTranslation } from "react-i18next";
import { nextSlotJoinCounts, type Draft, type DraftLineOptions, type Exercise } from "@heraion/shared";

import { EntryTipModal } from "../Logger/EntryTipModal";
import { GroupAlignDialog } from "../Logger/GroupAlignDialog";
import { SheetMenu } from "../primitives/SheetMenu";
import type { UiTheme } from "../theme";
import { exerciseMenuItems } from "./exerciseMenuItems";
import type { ExerciseEdit } from "./ExerciseView";
import { NextBlockSheet } from "./NextBlockSheet";
import { SupersetRenameDialog } from "./SupersetRenameDialog";
import type { V3Styles } from "./v3Styles";

// The exercise card's sheets and dialogs, in the editor only (#358: out of
// ExerciseView, which also reads a program with nothing to write): the menu,
// the superset's rename, the pad-or-trim question of a join with uneven set
// counts, «+ 4 rounds» and the «?» card, where an alternative is a replace.
export function ExerciseSheets({
  theme,
  styles,
  draft,
  sIdx,
  slotIdx,
  name,
  openRound,
  edit,
  lineOptions,
  resolveName,
  menuOpen,
  onCloseMenu,
  tipOpen,
  onCloseTip,
  blockOpen,
  onCloseBlock,
  onNote,
  onCopyToDays,
}: {
  theme: UiTheme;
  styles: V3Styles;
  draft: Draft;
  sIdx: number;
  slotIdx: number;
  name: string;
  openRound: number | null;
  edit: ExerciseEdit;
  lineOptions: DraftLineOptions;
  resolveName: (id: string, snapshot?: string) => string;
  menuOpen: boolean;
  onCloseMenu: () => void;
  tipOpen: boolean;
  onCloseTip: () => void;
  blockOpen: boolean;
  onCloseBlock: () => void;
  onNote: () => void;
  onCopyToDays: () => void;
}) {
  const { t } = useTranslation();
  const [renameOpen, setRenameOpen] = useState(false);
  const [alignAsk, setAlignAsk] = useState<ReturnType<typeof nextSlotJoinCounts>>(null);
  const { actions, tip, progression } = edit;
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  if (!slot) return null;
  const catalog = tip.exerciseCatalog ?? [];
  const groupId = slot.group?.id;
  // A join that meets different set counts asks first; equal counts just join.
  const pair = () => {
    const counts = nextSlotJoinCounts(draft, sIdx, slotIdx);
    if (counts) setAlignAsk(counts);
    else actions.pairWithNext(slotIdx);
  };

  return (
    <>
      {blockOpen ? (
        <NextBlockSheet
          theme={theme}
          styles={styles}
          draft={draft}
          sIdx={sIdx}
          lineOptions={lineOptions}
          resolveName={resolveName}
          apiFetch={edit.apiFetch}
          nextBlock={progression.nextBlock}
          onAdd={progression.addBlock}
          onClose={onCloseBlock}
        />
      ) : null}
      <SheetMenu
        theme={theme}
        visible={menuOpen}
        onClose={onCloseMenu}
        title={name}
        closeLabel={t("program.cancel", { defaultValue: "Cancel" })}
        items={exerciseMenuItems({
          t,
          draft,
          sIdx,
          slotIdx,
          openRound,
          actions,
          onReplace: edit.onReplace,
          onPair: pair,
          onRename: () => setRenameOpen(true),
          onNote,
          onCopyToDays,
        })}
      />
      <SupersetRenameDialog
        theme={theme}
        styles={styles}
        visible={renameOpen}
        current={slot.group?.name ?? ""}
        onSave={(text) => {
          if (groupId) actions.renameSuperset(groupId, text);
        }}
        onClose={() => setRenameOpen(false)}
      />
      {alignAsk ? (
        <GroupAlignDialog
          shortest={alignAsk.shortest}
          longest={alignAsk.longest}
          onPad={() => {
            actions.pairWithNext(slotIdx, "pad");
            setAlignAsk(null);
          }}
          onTrim={() => {
            actions.pairWithNext(slotIdx, "trim");
            setAlignAsk(null);
          }}
          onCancel={() => setAlignAsk(null)}
          // #336 S1: rounds that pad or trim to different counts get no number.
          {...(alignAsk.sameInEveryRound
            ? {}
            : {
                padLabel: t("editorV3.alignPadEachRound", { defaultValue: "+ Pad to the longest in each round" }),
                trimLabel: t("editorV3.alignTrimEachRound", { defaultValue: "− Trim to the shortest in each round" }),
              })}
          hideTrimWarning
          trimHint={t("editorV3.alignTrimHint", {
            defaultValue: "Removes the extra sets at the end. Undo in the message brings them back.",
          })}
        />
      ) : null}
      <EntryTipModal
        visible={tipOpen}
        exercise={catalog.find((ex) => ex.slug === slot.exerciseId) ?? null}
        catalog={catalog}
        planNote={slot.instructions}
        onClose={onCloseTip}
        onPickAlternative={(alt: Exercise) => {
          actions.replace(slotIdx, alt.slug);
          onCloseTip();
        }}
        profile={tip.profile}
        animationSource={tip.animationSource}
        onAnimationLoadError={tip.onAnimationLoadError}
      />
    </>
  );
}
